import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react"
import { DndContext, rectIntersection } from "@dnd-kit/core"
import { restrictToWindowEdges } from "@dnd-kit/modifiers"
import moment from "moment"
import { useNavigate, useParams } from "react-router-dom"
import {
  useStartTestRunMutation,
  useSaveTestRunProgressMutation,
  useCompleteTestRunMutation,
} from "../app/services/meseeknaApiV2"
import { RootState, useTypedDispatch, useTypedSelector } from "../app/store"
import MessagesWithSearch from "../components/messages-with-search"
import SimulationHeader from "../components/simulation-header"
import DecisionPlanSection from "../features/simulation-decisions/components/decision-plan-section"
import ModalBreaktime from "../features/simulation/components/modal-breaktime"
import { simulationSlice } from "../features/simulation/simulationSlice"
import {
  createTimer,
  destroyTimer,
  resetSimulationSafe,
  resumeTimer,
} from "../features/simulation/simulationThunks"
import { resetCollaboration } from "../features/collaboration/collaborationSlice"
import { SavePointData } from "../features/simulation/types"
import {
  convertToSeconds,
  resetSimulationState,
} from "../features/simulation/utils"
import { SAVEPOINT_TIMER } from "../utils/constants"
import LogoFull from "../components/logo-full"
import FinishedModal from "../components/finished-modal"

/**
 * Test Session Simulation Page
 *
 * This page renders the full simulation UI for test sessions.
 * It transforms V2 simulation data into the legacy format expected
 * by the simulation components.
 */
const TestSessionSimulation: FunctionComponent = () => {
  const dispatch = useTypedDispatch()
  const navigate = useNavigate()
  const { testSessionId } = useParams()

  const [isAppReady, setIsAppReady] = useState(false)
  const [initializationStep, setInitializationStep] = useState("loading")
  const [runId, setRunId] = useState<string | null>(null)
  const [runNumber, setRunNumber] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [simResponse, setSimResponse] = useState<{ data: Array<{ id: number; contents: string; title?: string; manualDescription?: string }> } | null>(null)

  const [startTestRun] = useStartTestRunMutation()
  const [saveTestRunProgress] = useSaveTestRunProgressMutation()
  const [completeTestRun] = useCompleteTestRunMutation()

  const participantId = localStorage.getItem("testSessionParticipantId")

  const [isModalBreaktimeOpen, setModalBreaktimeOpen] = useState(false)

  const { duration, timer, secondsElapsed, messages } = useTypedSelector(
    (state: RootState) => state.simulationSlice
  )

  // Track hydration state
  const [isHydrated, setIsHydrated] = useState(false)
  const hasSkippedFirstEffect = useRef(false)

  // State for finished modal and saving final results
  const [showFinishedModal, setShowFinishedModal] = useState(false)
  const [isSavingFinal, setIsSavingFinal] = useState(false)
  const [saveFinalError, setSaveFinalError] = useState<string | null>(null)

  const initialFinalData: SavePointData = {
    id: 0,
    startDate: "",
    endDate: "",
    length: "",
    time: "",
    name: "",
    log: [],
  }
  const [finalData, setFinalData] = useState(initialFinalData)

  /**
   * Transform V2 finalSimulationData to legacy API response format
   * The legacy format expects: { data: [{ id, contents: JSON string, title, manualDescription }] }
   */
  const transformToLegacyFormat = useCallback((v2Data: Record<string, unknown>) => {
    if (v2Data.data && Array.isArray(v2Data.data)) {
      return v2Data as {
        data: Array<{ id: number; contents: string; title?: string; manualDescription?: string }>
      }
    }

    // Check if this is already in legacy format (has 'map' with 'plots')
    if (v2Data.map && typeof v2Data.map === 'object' && 'plots' in (v2Data.map as object)) {
      // Already in legacy format, just wrap it
      return {
        data: [{
          id: (v2Data.id as number) || 1,
          contents: JSON.stringify(v2Data),
          title: (v2Data.name as string) || "Test Simulation",
          manualDescription: (v2Data.description as string) || "",
        }]
      }
    }

    // V2 format - transform it
    // V2 finalSimulationData has: id, name, description, length, emergencyStart, emergencyEnd, actionset
    const legacyContents = {
      length: (v2Data.length as string) || "21:00",
      emergencyStart: (v2Data.emergencyStart as string) || "",
      emergencyEnd: (v2Data.emergencyEnd as string) || "",
      map: v2Data.map || { plots: {} },
      actionset: v2Data.actionset || { categories: [] },
    }

    return {
      data: [{
        id: (v2Data.id as number) || 1,
        contents: JSON.stringify(legacyContents),
        title: (v2Data.name as string) || "Test Simulation",
        manualDescription: (v2Data.description as string) || "",
      }]
    }
  }, [])

  // Initialize test session simulation
  useEffect(() => {
    const initializeSimulation = async () => {
      try {
        setInitializationStep("loading-data")

        const storedSimData = localStorage.getItem("testSessionSimulationData")
        if (!storedSimData) {
          setError("No simulation data found")
          navigate(`/test-sessions/${testSessionId}/login`)
          return
        }

        const simData = JSON.parse(storedSimData)

        // Transform to legacy format for setupSimulation
        const legacyFormat = transformToLegacyFormat(simData)
        setSimResponse(legacyFormat)

        setInitializationStep("starting-run")

        if (testSessionId && participantId) {
          try {
            const runResult = await startTestRun({
              testSessionId,
              participantId,
            }).unwrap()

            setRunId(runResult.runId)
            setRunNumber(runResult.runNumber)
          } catch (err) {
            console.error("Failed to start test run:", err)
          }
        }

        // Reset simulation state before setup
        dispatch(resetSimulationSafe())

        // Reset collaboration state to ensure individual simulation mode
        // This prevents "team decision" UI from appearing in quick test
        dispatch(resetCollaboration())

        // Setup simulation with the transformed data
        dispatch(simulationSlice.actions.setupSimulation(legacyFormat))

        // Set final data
        setFinalData((prevFinalData) => ({
          ...prevFinalData,
          id: legacyFormat.data[0].id,
          name: legacyFormat.data[0].title || "",
          startDate: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
          time: "00:00:00",
        }))

        setInitializationStep("ready")
        setIsHydrated(true)
        hasSkippedFirstEffect.current = false
        setIsAppReady(true)
      } catch (err) {
        console.error("Failed to initialize test simulation:", err)
        setError("Failed to load simulation data")
        setInitializationStep("error")
      }
    }

    initializeSimulation()

    return () => {
      // Cleanup on unmount
      resetSimulationState(dispatch, false)
    }
  }, [testSessionId, participantId, navigate, startTestRun, dispatch, transformToLegacyFormat])

  // Create timer when simulation is ready
  // NOTE: Do NOT include timer in deps - it causes infinite destroy/recreate cycles
  // The createTimer thunk handles checking for existing timers internally
  useEffect(() => {
    if (!isAppReady) return
    console.debug("creating timer for test session")
    dispatch(createTimer())
    return () => {
      dispatch(destroyTimer())
    }
  }, [isAppReady, dispatch])

  // Monitor simulation time
  useEffect(() => {
    if (secondsElapsed === 0) return
    if (!isAppReady) return
    if (!timer) return

    const durationSeconds = convertToSeconds(duration)
    if (durationSeconds === 0) return

    // Save progress periodically
    if (secondsElapsed > 0 && secondsElapsed % SAVEPOINT_TIMER === 0) {
      saveDataPointDetails()
    }

    // Check if simulation is finished
    if (secondsElapsed >= durationSeconds) {
      setShowFinishedModal(true)
      if (!isSavingFinal && !saveFinalError) {
        saveFinalDataPoint()
      }
    }
  }, [secondsElapsed, isAppReady, timer, duration, isSavingFinal, saveFinalError])

  // Save on message changes
  useEffect(() => {
    if (!isHydrated) return
    if (!hasSkippedFirstEffect.current) {
      hasSkippedFirstEffect.current = true
      return
    }
    saveDataPointDetails()
  }, [messages, isHydrated])

  const saveDataPointDetails = async () => {
    if (!runId) return

    const time = moment("2024-01-01").seconds(secondsElapsed).format("HH:mm:ss")
    const saveData = {
      ...finalData,
      length: duration,
      time,
      log: [...messages],
    }

    try {
      await saveTestRunProgress({
        runId,
        savePointData: saveData as unknown as Record<string, unknown>,
        elapsedSeconds: secondsElapsed,
      }).unwrap()
    } catch (error) {
      console.error("Failed to save test run progress:", error)
    }
  }

  const saveFinalDataPoint = async () => {
    if (!runId) return

    setIsSavingFinal(true)
    setSaveFinalError(null)

    const final = {
      ...finalData,
      endDate: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
      log: [...messages],
      time: "",
    }

    try {
      await completeTestRun({
        runId,
        finalResultData: final as unknown as Record<string, unknown>,
        elapsedSeconds: secondsElapsed,
      }).unwrap()

      setIsSavingFinal(false)
      dispatch(destroyTimer())
      resetSimulationState(dispatch, false)
    } catch (error: unknown) {
      setIsSavingFinal(false)
      let message = "There was a problem saving your results. Please try again."
      if (error instanceof Error) {
        message = error.message
      }
      setSaveFinalError(message)
    }
  }

  const handleRetryFinalSave = () => {
    saveFinalDataPoint()
  }

  const closeModalBreaktime = useCallback(() => {
    dispatch(resumeTimer())
    setModalBreaktimeOpen(false)
  }, [dispatch])

  const handleFinish = useCallback(() => {
    // Clear test session data
    localStorage.removeItem("testSessionSimulationData")
    localStorage.removeItem("testSessionToken")
    localStorage.removeItem("testSessionId")
    localStorage.removeItem("testSessionParticipantId")
    localStorage.removeItem("testSessionUserId")
    localStorage.removeItem("testSessionFirstName")
    localStorage.removeItem("testSessionLastName")
    localStorage.removeItem("testSessionSimulationType")

    navigate(`/test-sessions/${testSessionId}/login`)
  }, [testSessionId, navigate])

  // Loading state
  if (!isAppReady) {
    return (
      <div className="relative bg-base-white w-full min-h-screen flex flex-col items-center justify-center gap-4 p-8">
        <LogoFull />
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm">
            {initializationStep === "loading-data" && "Loading simulation..."}
            {initializationStep === "starting-run" && "Starting test run..."}
            {initializationStep === "error" && (error || "Error loading simulation")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] overflow-hidden flex flex-col font-text-md-regular">
      {/* Test Session Banner */}
      <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogoFull />
          <span className="text-yellow-800 text-sm font-medium">
            Test Session - Run #{runNumber}
          </span>
        </div>
        <button
          onClick={handleFinish}
          className="px-3 py-1 text-sm text-yellow-800 hover:text-yellow-900 hover:bg-yellow-200 rounded transition-colors"
        >
          Exit Test
        </button>
      </div>

      {/* Simulation Content */}
      <div className="w-full flex-1 flex flex-col overflow-hidden">
        <div
          id="simulationContainer"
          className="relative overflow-hidden box-border w-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium"
        >
          <SimulationHeader
            setModalBreaktimeOpen={setModalBreaktimeOpen}
            simulation={simResponse?.data || []}
          />
          <div className="w-full overflow-hidden flex-1 flex flex-col items-start justify-start py-0 px-0 box-border gap-[12px] text-base text-gray-700">
            <DndContext
              autoScroll={false}
              collisionDetection={rectIntersection}
              modifiers={[restrictToWindowEdges]}
            >
              <div
                id="boxesContainer"
                className="self-stretch overflow-hidden flex-1 flex flex-row items-start gap-[19px] text-sm text-gray-500"
              >
                <MessagesWithSearch />
                <DecisionPlanSection />
              </div>
            </DndContext>
          </div>
        </div>
      </div>

      {/* Modals */}
      {isModalBreaktimeOpen && (
        <ModalBreaktime closeModalBreaktime={closeModalBreaktime} />
      )}
      {showFinishedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <FinishedModal
            isSaving={isSavingFinal}
            saveError={saveFinalError}
            onRetry={handleRetryFinalSave}
          />
        </div>
      )}
    </div>
  )
}

export default TestSessionSimulation
